'use client'

import { createContext, RefObject } from 'react'
import { ChatMessage } from '@/types'
import type { ChatSendOptions, ChatSettings, ChatStopOptions } from '@/types/chat'
import { Tables } from '@/supabase/types'
import type { SidekickSpaceItemContext } from '@/lib/sidekick/space-item-context'

export type StructuredMentionMode = 'crm' | 'space-member' | 'unified-chat'

export type StructuredMentionSuggestion = {
  avatarUrl?: string | null
  category?: string
  description?: string | null
  id: string
  display: string
  kind?:
    | 'assistant'
    | 'company'
    | 'contact'
    | 'everyone'
    | 'lead'
    | 'member'
    | 'platform'
    | 'super_agent'
}

export interface StructuredMentionConfig {
  mode: StructuredMentionMode
  markup?: string
  trigger?: string
  allowEmptyQuery?: boolean
  getSuggestions: (query: string) => Promise<StructuredMentionSuggestion[]>
}

/**
 * Optional override for chat send/stop/focus used by Sidekick (or other
 * embedded chat UIs). When provided, ChatInput uses this instead of
 * useChatHandler() so the same ChatInput component can drive a different
 * backend. Placeholder list and @ behavior (CRM mentions vs assistant picker)
 * can be overridden per context.
 */
export interface ChatHandlerOverride {
  /** When set (e.g. Sidekick), show "Talking to {name}" with this assistant; hidden from agents list */
  selectedAssistant?: Tables<'assistants'> | null
  handleSendMessage: (
    message: string,
    chatMessages: ChatMessage[],
    isUserMessage: boolean,
    options?: ChatSendOptions
  ) => void | Promise<void>
  handleStopMessage: (options?: ChatStopOptions) => void
  handleFocusChatInput: () => void
  chatInputRef: RefObject<HTMLTextAreaElement | null> | null
  /** When set, use these placeholder strings instead of default list */
  placeholderTexts?: string[]
  /** When true, @ opens CRM mention picker (leads/contacts/companies) instead of assistant autocomplete */
  useCrmMentions?: boolean
  /** When set, use this workspace ID for CRM mention fetch (e.g. Sidekick page context) */
  crmMentionWorkspaceId?: string | null
  /** When set, use structured mentions instead of assistant autocomplete */
  mentionConfig?: StructuredMentionConfig | null
  /** When set, show a linked Space item chip above the input */
  spaceItemContext?: SidekickSpaceItemContext | null
  /** Clear the linked Space item chip */
  clearSpaceItemContext?: () => void
  /**
   * Opt-in: keep tool controls visible in ChatInput even when a handler
   * override is active.
   */
  keepToolControlsVisible?: boolean
  /**
   * Opt-in typing presence for embedded chats backed by a real chat row.
   */
  enableTypingPresence?: boolean
  /**
   * Optional chat settings fallback used by upload/paste helpers when the
   * scoped chat has not hydrated yet (for example Sidekick draft chats).
   */
  chatSettings?: ChatSettings | null
}

export const ChatHandlerContext = createContext<ChatHandlerOverride | null>(null)
